import {
  EasingDirection,
  EasingType,
  clamp,
  easing,
  lerp,
  lerpMap,
  quantize,
  rangeMax,
  type Range
} from '@utils';

export interface FontSizeChooser {
  getFontSize(text: string): number;
  clone(): FontSizeChooser;
  toJSON(): unknown;
}

export class FixedFontSizeChooser implements FontSizeChooser {
  static DEFAULT = new FixedFontSizeChooser(18);

  private fontSize: number;

  constructor(fontSize: number) {
    this.fontSize = fontSize;
  }

  get fontSizeValue(): number {
    return this.fontSize;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getFontSize(text: string): number {
    return this.fontSize;
  }

  clone(): FixedFontSizeChooser {
    return new FixedFontSizeChooser(this.fontSize);
  }

  toJSON(): unknown {
    return {
      type: 'FixedFontSizeChooser',
      fontSize: this.fontSize
    };
  }
}

export class ScalingFontSizeChooser implements FontSizeChooser {
  static DEFAULT = new ScalingFontSizeChooser([20, 12], [4, 80]);

  private fontSizeRange: Range;
  private taperCharCountRange: Range;

  private static EASE_OUT = easing(EasingDirection.OUT, EasingType.QUADRATIC);

  constructor(fontSizeRange: Range, taperCharCountRange: Range) {
    this.fontSizeRange = fontSizeRange;
    this.taperCharCountRange = taperCharCountRange;
  }

  get fontSizeRangeValue(): Range {
    return this.fontSizeRange;
  }

  get taperCharCountRangeValue(): Range {
    return this.taperCharCountRange;
  }

  getFontSize(text: string): number {
    let size = rangeMax(this.fontSizeRange);
    const length = text.length;

    if (length > 0) {
      let fraction = lerpMap(length, this.taperCharCountRange, [0, 1]);

      fraction = clamp(fraction, [0, 1]);
      fraction = ScalingFontSizeChooser.EASE_OUT(fraction);
      size = lerp(fraction, this.fontSizeRange);
      size = quantize(size, 0.5);
    }

    return size;
  }

  clone(): ScalingFontSizeChooser {
    return new ScalingFontSizeChooser(
      [...this.fontSizeRange],
      [...this.taperCharCountRange]
    );
  }

  toJSON(): unknown {
    return {
      type: 'ScalingFontSizeChooser',
      fontSizeRange: this.fontSizeRange,
      taperCharCountRange: this.taperCharCountRange
    };
  }
}
